import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import VerifyAccountModal from "./VerifyAccountModal";
import { kycUiStore } from "@/lib/kycUiState";

const FLAG_KEY = "bera:show_verify_modal";
const HIDDEN_PREFIXES = ["/auth", "/onboarding", "/admin"];

/**
 * Headless gate — after a successful login, shows the KYC welcome modal
 * once when the profile confirms the user is not verified yet.
 */
export function PostLoginVerifyGate() {
  const { user } = useAuth();
  const { profile, isLoading, isVerified, isKycApproved, hasSubmittedVerification, isPrivilegedAdmin, needsVerification } = useUserProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const closeGate = useCallback(() => {
    setOpen(false);
    kycUiStore.setOpen(false);
    try {
      document.body.style.pointerEvents = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    } catch {}
  }, []);

  useEffect(() => {
    if (!user || isLoading || !profile) return;
    // Admins/super admins and any already-verified account never see the KYC modal.
    if (isPrivilegedAdmin || isVerified || isKycApproved || !needsVerification) {
      closeGate();
      try {
        sessionStorage.removeItem(FLAG_KEY);
      } catch {}
      return;
    }

    let flag: string | null = null;
    try {
      flag = sessionStorage.getItem(FLAG_KEY);
    } catch {}
    if (flag !== "1") return;

    // Do not show on excluded routes
    if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return;

    if (!hasSubmittedVerification && needsVerification) {
      setOpen(true);
    }
    // Clear the flag either way so it only fires once per login
    try {
      sessionStorage.removeItem(FLAG_KEY);
    } catch {}
  }, [user, profile, isLoading, isVerified, isKycApproved, hasSubmittedVerification, isPrivilegedAdmin, needsVerification, location.pathname, closeGate]);

  if (!user || isPrivilegedAdmin || isVerified || isKycApproved || !needsVerification) return null;

  return (
    <VerifyAccountModal
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen && needsVerification) setOpen(true);
        else closeGate();
      }}
      userName={profile?.full_name}
    />
  );
}

export default PostLoginVerifyGate;
